/**
 * Workspace autosave (issue 187).
 *
 * Persists the open, non-detached panels to localStorage on a 2s leading +
 * trailing throttle.  On next load, the Shell renders a recovery banner if a
 * snapshot exists and the workspace is currently empty.  Users must explicitly
 * opt in to restore (human-in-the-loop rule).
 *
 * The autosave buffer is distinct from named saved layouts — it is a
 * temporary recovery cache cleared after restore or dismiss.
 */
import type { PanelDescriptor } from '@/screens/types';
import { throttleLeadingTrailing, type ThrottleHandle } from '@/shared/throttle';
import {
  buildLayoutConfigFromPanels,
  hydratePanelsFromLayoutConfig,
  parseLayoutConfig,
} from '@/templates/layoutTemplates';
import { usePanelStore } from './panelStore';

export const AUTOSAVE_STORAGE_KEY = 'trms_workbench_autosave_v1';
export const AUTOSAVE_THROTTLE_MS = 2_000;

function safeStorage(): Storage | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Read the current autosave buffer; null if missing or unparseable. */
export function readAutosaveSnapshot(): ReturnType<typeof parseLayoutConfig> {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(AUTOSAVE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return parseLayoutConfig(parsed);
  } catch (err) {
    console.warn('[autosave] failed to read snapshot', err);
    return null;
  }
}

/** Write a snapshot now (bypasses the throttle). */
export function writeAutosaveSnapshotNow(panels: PanelDescriptor[]): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    const workspace = panels.filter((p) => !p.detached);
    if (workspace.length === 0) {
      storage.removeItem(AUTOSAVE_STORAGE_KEY);
      return;
    }
    const config = buildLayoutConfigFromPanels(workspace);
    storage.setItem(AUTOSAVE_STORAGE_KEY, JSON.stringify(config));
  } catch (err) {
    console.warn('[autosave] failed to write snapshot', err);
  }
}

/** Clear the autosave buffer. */
export function clearAutosaveSnapshot(): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    storage.removeItem(AUTOSAVE_STORAGE_KEY);
  } catch (err) {
    console.warn('[autosave] failed to clear snapshot', err);
  }
}

let installed = false;
let unsubStore: (() => void) | null = null;
let beforeUnloadHandler: ((event: BeforeUnloadEvent) => void) | null = null;
let throttledWrite:
  | (((panels: PanelDescriptor[]) => void) & ThrottleHandle)
  | null = null;

/**
 * Install the autosave subscription. Idempotent — safe to call multiple times.
 * Returns a teardown function for tests.
 */
export function installAutosave(throttleMs: number = AUTOSAVE_THROTTLE_MS): () => void {
  if (installed) {
    return uninstallAutosave;
  }
  installed = true;

  throttledWrite = throttleLeadingTrailing(writeAutosaveSnapshotNow, throttleMs);

  let lastPanels = usePanelStore.getState().panels;
  unsubStore = usePanelStore.subscribe((state) => {
    if (state.panels === lastPanels) return;
    lastPanels = state.panels;
    throttledWrite?.(state.panels);
  });

  if (typeof window !== 'undefined') {
    beforeUnloadHandler = () => {
      throttledWrite?.flush();
      // Best-effort synchronous write on tab close.
      writeAutosaveSnapshotNow(usePanelStore.getState().panels);
    };
    window.addEventListener('beforeunload', beforeUnloadHandler);
  }

  return uninstallAutosave;
}

export function uninstallAutosave(): void {
  if (!installed) return;
  installed = false;
  unsubStore?.();
  unsubStore = null;
  throttledWrite?.cancel();
  throttledWrite = null;
  if (typeof window !== 'undefined' && beforeUnloadHandler) {
    window.removeEventListener('beforeunload', beforeUnloadHandler);
    beforeUnloadHandler = null;
  }
}

/** True when an autosave buffer is present and parseable. */
export function hasAutosaveSnapshot(): boolean {
  return readAutosaveSnapshot() !== null;
}

/**
 * Restore the snapshot into the panel store. Caller is responsible for
 * deciding whether the workspace is empty (the recovery banner enforces
 * this); restore unconditionally replaces workspace panels.
 */
export function restoreAutosave(): { restored: number } {
  const config = readAutosaveSnapshot();
  if (!config) return { restored: 0 };
  const { panels } = hydratePanelsFromLayoutConfig(config);
  usePanelStore.getState().replaceWorkspacePanels(panels);
  clearAutosaveSnapshot();
  return { restored: panels.length };
}

/** Dismiss (clear) the snapshot without restoring. */
export function dismissAutosave(): void {
  clearAutosaveSnapshot();
}
